import { useMemo, useState, type DragEvent, type MouseEvent as ReactMouseEvent } from 'react';

import { partAt, partPathOf, resolvePartPath, SHAPE_DEFAULTS, countParts, type IndexPath, type ModelDoc } from '../../lib/model-doc.js';
import { addBlock, PART_BLOCKS } from './part-blocks.js';
import {
  allPartPaths,
  canDropParts,
  descendantPartPaths,
  effectiveSelection,
  filterPartRows,
  groupParts,
  isolateParts,
  moveParts,
  parentToActive,
  partPathsOf,
  rangeSelect,
  renamePart,
  revealAll,
  selectionRoots,
  setPartsFlag,
  unparentParts,
  type DropPosition,
} from '../../lib/model-tree.js';
import { useCommands } from '../../lib/shortcuts/index.js';
import { useModel } from '../../state/model.js';
import { ContextMenu, IconButton, MenuButton, type MenuItem } from '../ui/controls.js';
import { Icon, type IconName } from '../ui/icons.js';
import { modelOp } from './OpStack.js';
import './parts-tree.css';

/**
 * The Model tab's Parts panel (Blender outliner style): multi-select (Ctrl /
 * Shift click), drag to reparent or reorder, search, inline rename, per-row
 * hide and lock toggles, and batch actions on the selection. Every action is
 * one `useModel.update` call, so one undo step.
 */

export const SHAPE_ICON: Record<string, IconName> = {
  box: 'cube',
  sphere: 'sphere',
  capsule: 'cube',
  cylinder: 'cube',
  cone: 'model',
  torus: 'smooth',
  plane: 'grid',
  lathe: 'rotate',
  extrude: 'layers',
  tube: 'link',
  mesh: 'wireframe',
  blob: 'particles',
  sculpt: 'pencil',
  sweep: 'line', loft: 'circle', tree: 'mountain', skin: 'target', hair: 'wand', cloth: 'square', garment: 'tag',
  empty: 'empty',
};

export function addPartItems(asChild: boolean): MenuItem[] {
  return [
    { id: 'h', label: asChild ? 'Add child part' : 'Add part', heading: true },
    ...Object.keys(SHAPE_DEFAULTS).map((type) => ({
      id: type,
      label: type[0]!.toUpperCase() + type.slice(1),
      icon: SHAPE_ICON[type] ?? 'cube',
      run: () => {
        const doc = useModel.getState().doc;
        useModel.getState().addPart(
          {
            name: type === 'empty' ? 'group' : type,
            ...(doc?.materials && Object.keys(doc.materials).length ? { material: Object.keys(doc.materials)[0] } : {}),
            shape: { type, ...SHAPE_DEFAULTS[type] },
          },
          asChild,
        );
      },
    })),
    { id: 'blocks-h', label: 'Building blocks', heading: true },
    ...PART_BLOCKS.map((block) => ({ id: `block-${block.id}`, label: block.label, icon: block.icon, run: () => addBlock(block, asChild) })),
  ];
}

/** Lock glyph (the shared icon set has none). */
function LockGlyph({ open, size = 13 }: { open: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="icon">
      <rect x="5" y="11" width="14" height="9.5" rx="2" />
      <path d={open ? 'M8 11V7.5a4 4 0 0 1 7.6-1.7' : 'M8 11V7.5a4 4 0 0 1 8 0V11'} />
    </svg>
  );
}

const KEEP_WORLD_KEY = 'prism.model.reparentKeepWorld';
function readKeepWorld(): boolean {
  try {
    return localStorage.getItem(KEEP_WORLD_KEY) !== 'false';
  } catch {
    return true;
  }
}

/* ------------------------------------------------------------------ */
/* Actions on the current selection (fresh store state every call)     */

function current(): { doc: ModelDoc; parts: string[]; active: string | null } | null {
  const s = useModel.getState();
  if (!s.doc) return null;
  return { doc: s.doc, parts: effectiveSelection(s.selected, s.multi), active: s.selected };
}

/** Selection as index paths before an edit that keeps the tree's shape → part paths after it. */
function keepSelection(before: ModelDoc, after: ModelDoc, parts: string[], active: string | null): [IndexPath | null, string[]] {
  const map = (p: string) => {
    const index = resolvePartPath(before, p);
    return index ? partPathOf(after, index) : null;
  };
  const activeIndex = active ? resolvePartPath(before, active) : null;
  return [activeIndex, parts.map(map).filter((p): p is string => p !== null)];
}

/** Commit a structural result: select the moved/created parts, the active one among them. */
function commitMoved(before: { parts: string[]; active: string | null; doc: ModelDoc }, result: { doc: ModelDoc; paths: number[][] } | null) {
  if (!result) return;
  const roots = selectionRoots(before.doc, before.parts);
  const activeAt = before.active ? roots.indexOf(before.active) : -1;
  const active = result.paths[activeAt] ?? result.paths.at(-1) ?? null;
  useModel.getState().update(result.doc, active, partPathsOf(result.doc, result.paths));
}

function setFlag(key: 'hidden' | 'locked', on: boolean, parts?: string[]) {
  const c = current();
  if (!c) return;
  const targets = parts ?? c.parts;
  if (!targets.length) return;
  useModel.getState().update(setPartsFlag(c.doc, targets, key, on));
}

/** Toggle a flag on the parts: set it unless every one already has it. */
function toggleFlag(key: 'hidden' | 'locked', parts?: string[]) {
  const c = current();
  if (!c) return;
  const targets = parts ?? c.parts;
  const all = targets.every((p) => {
    const index = resolvePartPath(c.doc, p);
    return Boolean(index && partAt(c.doc, index)?.[key]);
  });
  setFlag(key, !all, targets);
}

export const partsActions = {
  hide: () => setFlag('hidden', true),
  toggleHidden: () => toggleFlag('hidden'),
  toggleLocked: () => toggleFlag('locked'),
  reveal: () => {
    const c = current();
    if (c) useModel.getState().update(revealAll(c.doc));
  },
  isolate: () => {
    const c = current();
    if (c && c.parts.length) useModel.getState().update(isolateParts(c.doc, c.parts));
  },
  selectAll: () => {
    const c = current();
    if (!c) return;
    const all = allPartPaths(c.doc);
    useModel.getState().selectMany(all, c.active && all.includes(c.active) ? c.active : (all[0] ?? null));
  },
  selectChildren: () => {
    const c = current();
    if (!c || !c.parts.length) return;
    const extra = c.parts.flatMap((p) => descendantPartPaths(c.doc, p));
    useModel.getState().selectMany([...new Set([...c.parts, ...extra])], c.active);
  },
  group: () => {
    const c = current();
    if (!c || !c.parts.length) return;
    const result = groupParts(c.doc, c.parts);
    if (!result) return;
    const group = partPathOf(result.doc, result.group);
    useModel.getState().update(result.doc, result.group, [group]);
  },
  unparent: (keepWorld = true) => {
    const c = current();
    if (c) commitMoved(c, unparentParts(c.doc, c.parts, keepWorld));
  },
  parentToActive: (keepWorld = true) => {
    const c = current();
    if (!c || !c.active) return;
    const result = parentToActive(c.doc, c.parts, c.active, keepWorld);
    if (!result) return;
    // The active part stays selected (now the parent of the others).
    const moved = partPathsOf(result.doc, result.paths);
    const parentIndex = result.paths[0]?.slice(0, -1) ?? null;
    useModel.getState().update(result.doc, parentIndex, parentIndex ? [...moved, partPathOf(result.doc, parentIndex)] : moved);
  },
  rename: (partPath: string, name: string) => {
    const c = current();
    if (!c) return;
    const result = renamePart(c.doc, partPath, name);
    if (result.doc === c.doc) return;
    const [active, multi] = keepSelection(c.doc, result.doc, c.parts, c.active);
    useModel.getState().update(result.doc, active, multi);
  },
  applyAllOps: () => {
    const c = current();
    if (!c) return;
    for (const p of c.parts) {
      const index = resolvePartPath(c.doc, p);
      const count = index ? (partAt(c.doc, index)?.ops?.length ?? 0) : 0;
      if (count) modelOp('applyOps', { path: p, count });
    }
  },
  move: (sources: string[], target: string | null, position: DropPosition, keepWorld: boolean) => {
    const c = current();
    if (!c) return;
    const parts = sources.length ? sources : c.parts;
    commitMoved({ doc: c.doc, parts, active: parts.includes(c.active ?? '') ? c.active : null }, moveParts(c.doc, parts, target, position, keepWorld));
  },
};

/** Drop position from the pointer's height in the row: top quarter before, bottom quarter after, else into. */
function dropPosition(event: DragEvent<HTMLElement>): DropPosition {
  const r = event.currentTarget.getBoundingClientRect();
  const y = (event.clientY - r.top) / Math.max(1, r.height);
  return y < 0.25 ? 'before' : y > 0.75 ? 'after' : 'into';
}

const DRAG_TYPE = 'application/x-prism-parts';

/* ------------------------------------------------------------------ */

export function PartsTree() {
  const doc = useModel((s) => s.doc);
  const selected = useModel((s) => s.selected);
  const multi = useModel((s) => s.multi);
  const collapsedList = useModel((s) => s.collapsed);
  const toggleCollapsed = useModel((s) => s.toggleCollapsed);
  const [filter, setFilter] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [anchor, setAnchor] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string[] | null>(null);
  const [drop, setDrop] = useState<{ target: string | null; position: DropPosition } | null>(null);
  const [keepWorld, setKeepWorldState] = useState(readKeepWorld);
  const collapsed = useMemo(() => new Set(collapsedList), [collapsedList]);
  const rows = useMemo(() => (doc ? filterPartRows(doc, filter, collapsed) : []), [doc, filter, collapsed]);
  const selection = useMemo(() => effectiveSelection(selected, multi), [selected, multi]);
  const selSet = useMemo(() => new Set(selection), [selection]);

  // Hidden / locked including what the row inherits from its ancestors.
  const inherited = useMemo(() => {
    const out = new Map<string, { hidden: boolean; locked: boolean }>();
    for (const row of rows) {
      const parent = row.partPath.includes('/') ? out.get(row.partPath.slice(0, row.partPath.lastIndexOf('/'))) : undefined;
      out.set(row.partPath, {
        hidden: Boolean(row.part.hidden) || Boolean(parent?.hidden),
        locked: Boolean(row.part.locked) || Boolean(parent?.locked),
      });
    }
    return out;
  }, [rows]);

  const enabled = () => {
    const s = useModel.getState();
    return Boolean(s.doc) && !s.meshEdit.active && !s.sculpt.active;
  };
  const hasSelection = () => enabled() && Boolean(useModel.getState().selected);
  useCommands(
    [
      { id: 'model.parts.rename', label: 'Rename part', category: 'Model', context: 'model', keys: ['F2'], icon: 'edit', enabled: hasSelection, run: () => setRenaming(useModel.getState().selected) },
      { id: 'model.parts.hide', label: 'Hide selected parts', category: 'Model', context: 'model', keys: ['H'], icon: 'eyeOff', enabled: hasSelection, run: partsActions.hide },
      { id: 'model.parts.reveal', label: 'Reveal hidden parts', category: 'Model', context: 'model', keys: ['Alt+H'], icon: 'eye', enabled, run: partsActions.reveal },
      { id: 'model.parts.isolate', label: 'Hide unselected parts (isolate)', category: 'Model', context: 'model', keys: ['Shift+H'], icon: 'focus', enabled: hasSelection, run: partsActions.isolate },
      { id: 'model.parts.select-all', label: 'Select all parts', category: 'Model', context: 'model', keys: ['Ctrl+A'], enabled, run: partsActions.selectAll },
      { id: 'model.parts.group', label: 'Group selected parts into a new empty', category: 'Model', context: 'model', keys: ['Ctrl+Shift+G'], icon: 'empty', enabled: hasSelection, run: partsActions.group },
      { id: 'model.parts.unparent', label: 'Clear parent (keep transform)', category: 'Model', context: 'model', keys: ['Alt+P'], icon: 'arrowUp', enabled: hasSelection, run: () => partsActions.unparent(true) },
      { id: 'model.parts.lock', label: 'Lock / unlock selected parts', category: 'Model', context: 'model', keys: [], enabled: hasSelection, run: partsActions.toggleLocked },
      { id: 'model.parts.select-children', label: 'Select children', category: 'Model', context: 'model', keys: [], enabled: hasSelection, run: partsActions.selectChildren },
    ],
    [],
  );

  if (!doc) return null;
  const s = useModel.getState();

  const setKeepWorld = (on: boolean) => {
    setKeepWorldState(on);
    try {
      localStorage.setItem(KEEP_WORLD_KEY, String(on));
    } catch {
      // Storage unavailable: the choice lasts for this session.
    }
  };

  function onRowClick(partPath: string, event: ReactMouseEvent) {
    if (event.ctrlKey || event.metaKey) {
      if (selSet.has(partPath)) {
        const next = selection.filter((p) => p !== partPath);
        s.selectMany(next, selected === partPath ? (next.at(-1) ?? null) : selected);
      } else {
        s.selectMany([...selection, partPath], partPath);
      }
      setAnchor(partPath);
    } else if (event.shiftKey) {
      s.selectMany(rangeSelect(rows, anchor ?? selected, partPath), partPath);
    } else {
      s.select(partPath);
      setAnchor(partPath);
    }
  }

  function onDragStart(event: DragEvent<HTMLElement>, partPath: string) {
    let sources = selection;
    if (!selSet.has(partPath)) {
      s.select(partPath);
      sources = [partPath];
    }
    setDragging(sources);
    event.dataTransfer.setData(DRAG_TYPE, JSON.stringify(sources));
    event.dataTransfer.effectAllowed = 'move';
  }

  function onDragOverRow(event: DragEvent<HTMLElement>, partPath: string) {
    if (!dragging) return;
    event.stopPropagation();
    const position = dropPosition(event);
    if (!canDropParts(doc!, dragging, partPath, position)) {
      event.dataTransfer.dropEffect = 'none';
      setDrop(null);
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (drop?.target !== partPath || drop.position !== position) setDrop({ target: partPath, position });
  }

  function finishDrop(event: DragEvent<HTMLElement>) {
    event.preventDefault();
    event.stopPropagation();
    const target = drop;
    const sources = dragging;
    setDrop(null);
    setDragging(null);
    if (!target || !sources) return;
    partsActions.move(sources, target.target, target.position, keepWorld);
  }

  const count = selection.length;
  const many = count > 1;
  const activePart = (() => {
    const index = selected ? resolvePartPath(doc, selected) : null;
    return index ? partAt(doc, index) : null;
  })();
  const allHidden = count > 0 && selection.every((p) => inherited.get(p)?.hidden || partOf(doc, p)?.hidden);
  const allLocked = count > 0 && selection.every((p) => partOf(doc, p)?.locked);
  const withOps = selection.filter((p) => (partOf(doc, p)?.ops?.length ?? 0) > 0).length;
  const n = (label: string) => (many ? `${label} ${count}` : label);

  const items: MenuItem[] = [
    { id: 'rename', label: 'Rename', icon: 'edit', shortcut: 'F2', disabled: many, run: () => setRenaming(selected) },
    { id: 'dup', label: n('Duplicate'), icon: 'copy', shortcut: 'Ctrl+D', run: s.duplicateSelected },
    { id: 'del', label: n('Delete'), icon: 'trash', shortcut: 'Del', danger: true, run: s.deleteSelected },
    { id: 'sep1', label: '', separator: true },
    { id: 'hide', label: allHidden ? n('Show') : n('Hide'), icon: allHidden ? 'eye' : 'eyeOff', shortcut: allHidden ? undefined : 'H', run: partsActions.toggleHidden },
    { id: 'lock', label: allLocked ? n('Unlock') : n('Lock'), icon: 'target', run: partsActions.toggleLocked },
    { id: 'isolate', label: 'Isolate (hide others)', icon: 'focus', shortcut: 'Shift+H', run: partsActions.isolate },
    { id: 'reveal', label: 'Reveal all hidden', icon: 'eye', shortcut: 'Alt+H', run: partsActions.reveal },
    { id: 'sep2', label: '', separator: true },
    { id: 'children', label: 'Select children', icon: 'list', run: partsActions.selectChildren },
    { id: 'all', label: 'Select all', icon: 'list', shortcut: 'Ctrl+A', run: partsActions.selectAll },
    { id: 'sep3', label: '', separator: true },
    { id: 'group', label: many ? `Group ${count} into new empty` : 'Group into new empty', icon: 'empty', shortcut: 'Ctrl+Shift+G', run: partsActions.group },
    ...(many ? [{ id: 'parent', label: 'Parent to active', icon: 'link' as const, run: () => partsActions.parentToActive(true) }] : []),
    {
      id: 'unparent',
      label: 'Clear parent (keep transform)',
      icon: 'arrowUp',
      shortcut: 'Alt+P',
      disabled: !selection.some((p) => p.includes('/')),
      run: () => partsActions.unparent(true),
    },
    { id: 'up', label: 'Move up', icon: 'chevronUp', disabled: many, run: () => s.moveSelected(-1) },
    { id: 'down', label: 'Move down', icon: 'chevronDown', disabled: many, run: () => s.moveSelected(1) },
    { id: 'sep4', label: '', separator: true },
    {
      id: 'mesh',
      label: 'Convert to mesh',
      icon: 'wireframe',
      disabled: many || !activePart || activePart.shape?.type === 'mesh',
      run: s.convertToMesh,
    },
    { id: 'apply', label: many ? `Apply all ops (${withOps} parts)` : 'Apply all ops', icon: 'check', disabled: withOps === 0, run: partsActions.applyAllOps },
    { id: 'frame', label: 'Frame selected', icon: 'focus', shortcut: 'F', run: s.frameSelected },
    { id: 'sep5', label: '', separator: true },
    ...addPartItems(true),
  ];

  return (
    <div className="hierarchy parts-tree">
      <div className="panel-tools">
        <div className="search-input">
          <Icon name="search" size={13} />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search parts" aria-label="Search parts" />
          {filter && (
            <button type="button" className="parts-search-clear" title="Clear search" onClick={() => setFilter('')}>
              <Icon name="x" size={12} />
            </button>
          )}
        </div>
        <IconButton
          icon="globe"
          size={14}
          active={keepWorld}
          title={keepWorld ? 'Drag to reparent keeps world placement (click: keep local transforms)' : 'Drag to reparent keeps local transforms (click: keep world placement)'}
          onClick={() => setKeepWorld(!keepWorld)}
        />
        <MenuButton items={() => addPartItems(false)} className="ibtn" title="Add part">
          <Icon name="plus" size={15} />
        </MenuButton>
      </div>
      <ul
        className={`tree-list${drop && drop.target === null ? ' is-drop-root' : ''}`}
        role="tree"
        aria-multiselectable="true"
        onClick={(e) => e.target === e.currentTarget && s.select(null)}
        onContextMenu={(e) => {
          if ((e.target as HTMLElement).closest('.tree-item')) return;
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        onDragOver={(e) => {
          if (!dragging || (e.target as HTMLElement).closest('.tree-item')) return;
          if (!canDropParts(doc, dragging, null, 'into')) return;
          e.preventDefault();
          if (drop?.target !== null) setDrop({ target: null, position: 'into' });
        }}
        onDragLeave={(e) => {
          if (e.target === e.currentTarget) setDrop(null);
        }}
        onDrop={finishDrop}
      >
        {rows.map((row) => {
          const isSel = selSet.has(row.partPath);
          const isActive = selected === row.partPath;
          const flags = inherited.get(row.partPath);
          const dropHere = drop && drop.target === row.partPath ? ` drop-${drop.position}` : '';
          const rowTargets = isSel ? selection : [row.partPath];
          return (
            <li
              key={row.partPath}
              role="treeitem"
              aria-selected={isSel}
              aria-expanded={row.hasChildren ? !collapsed.has(row.partPath) : undefined}
              className={`tree-item${isSel ? ' is-selected' : ''}${isActive && many ? ' is-active' : ''}${flags?.hidden ? ' is-hidden' : ''}${flags?.locked ? ' is-locked' : ''}${filter && !row.match ? ' is-context' : ''}${dragging?.includes(row.partPath) ? ' is-dragging' : ''}${dropHere}`}
              style={{ paddingLeft: 6 + row.depth * 14 }}
              draggable={renaming !== row.partPath}
              onDragStart={(e) => onDragStart(e, row.partPath)}
              onDragOver={(e) => onDragOverRow(e, row.partPath)}
              onDragEnd={() => {
                setDragging(null);
                setDrop(null);
              }}
              onDrop={finishDrop}
              onClick={(e) => onRowClick(row.partPath, e)}
              onDoubleClick={(e) => {
                if ((e.target as HTMLElement).closest('button')) return;
                setRenaming(row.partPath);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                if (!isSel) s.select(row.partPath);
                setMenu({ x: e.clientX, y: e.clientY });
              }}
              title={row.partPath}
            >
              <button
                type="button"
                className={`tree-caret${row.hasChildren ? '' : ' is-leaf'}`}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.hasChildren && !filter) toggleCollapsed(row.partPath);
                }}
              >
                {row.hasChildren && <Icon name={collapsed.has(row.partPath) && !filter ? 'chevronRight' : 'chevronDown'} size={11} />}
              </button>
              <Icon name={SHAPE_ICON[row.part.shape?.type ?? 'empty'] ?? 'cube'} size={14} className="tree-kind kind-mesh" />
              {renaming === row.partPath ? (
                <input
                  className="tree-rename"
                  autoFocus
                  defaultValue={row.part.name ?? ''}
                  aria-label="Part name"
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.currentTarget.select()}
                  onBlur={(e) => {
                    if (renaming === row.partPath && e.target.value.trim() !== (row.part.name ?? '')) partsActions.rename(row.partPath, e.target.value);
                    setRenaming(null);
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') {
                      // Restore the name so the blur that follows does not commit.
                      (e.target as HTMLInputElement).value = row.part.name ?? '';
                      setRenaming(null);
                    }
                  }}
                />
              ) : (
                <span className="tree-label">{row.part.name || <em className="muted">unnamed</em>}</span>
              )}
              {row.part.ops?.length ? <span className="tree-meta">{row.part.ops.length} ops</span> : null}
              {row.part.array ? <span className="tree-meta">×{row.part.array.count ?? 1}</span> : null}
              <span className="parts-row-toggles">
                <button
                  type="button"
                  className={`parts-toggle${row.part.locked ? ' is-on' : ''}`}
                  tabIndex={-1}
                  title={`${row.part.locked ? 'Locked: not pickable or movable in the viewport' : 'Lock (not pickable or movable in the viewport)'}${isSel && many ? ' · applies to the selection' : ''}`}
                  aria-pressed={Boolean(row.part.locked)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFlag('locked', !row.part.locked, rowTargets);
                  }}
                >
                  <LockGlyph open={!row.part.locked} />
                </button>
                <button
                  type="button"
                  className={`parts-toggle${row.part.hidden ? ' is-on' : ''}`}
                  tabIndex={-1}
                  title={`${row.part.hidden ? 'Hidden (still usable as a boolean cutter)' : 'Hide'}${isSel && many ? ' · applies to the selection' : ''}`}
                  aria-pressed={Boolean(row.part.hidden)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFlag('hidden', !row.part.hidden, rowTargets);
                  }}
                >
                  <Icon name={row.part.hidden ? 'eyeOff' : 'eye'} size={13} />
                </button>
              </span>
            </li>
          );
        })}
        {rows.length === 0 && (
          <li className="tree-empty muted">
            {filter ? 'No matching parts.' : 'No parts yet. Add one with +, or describe the model to the AI below.'}
          </li>
        )}
      </ul>
      <div className="parts-tree-foot muted">
        {countParts(doc.parts)} parts · {Object.keys(doc.materials ?? {}).length} materials
        {many && <span className="parts-tree-count"> · {count} selected</span>}
      </div>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={selection.length ? items : addPartItems(false)} onClose={() => setMenu(null)} />}
    </div>
  );
}

function partOf(doc: ModelDoc, partPath: string) {
  const index = resolvePartPath(doc, partPath);
  return index ? partAt(doc, index) : null;
}
